/**
 * The last chats a person saw on each tab of an Account, kept in the browser
 * so the Chats page paints at once and OnlyFans' fresh answer replaces it
 * (ADR 0027 speed work). Only what a row needs is kept; the 30 most recently
 * used Accounts survive, and logout forgets them all.
 */
import type { Raw } from './chatRows'

export const LIST_CACHE_ACCOUNTS = 30
const KEEP_CHATS = 20
const TEXT_MAX = 300
const PREFIX = 'crm_chats_list_cache_'
const INDEX = 'crm_chats_list_cache_index'

export interface CachedTab {
  list: Raw[]
  hasMore: boolean
  nextOffset?: number
  savedAt: number
  /** users/list answers for these chats' fans, by fan id. */
  users: Record<string, Raw>
}

type Stored = {
  tabs: Record<string, Omit<CachedTab, 'users'>>
  users: Record<string, Raw>
}

const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {})

function pick(src: Raw, keys: string[]): Raw {
  const out: Raw = {}
  for (const k of keys) if (src[k] !== undefined) out[k] = src[k]
  return out
}

const CHAT_KEYS = [
  'withUserId',
  'fan_id',
  'unreadMessagesCount',
  'unread_messages_count',
  'hasUnread',
  'muted',
  'isMuted',
  'isMutedNotifications',
  'notificationsMuted',
  'updatedAt',
  'totalSpent',
  'role',
  'isCreator',
]
const USER_KEYS = ['id', 'name', 'displayName', 'username', 'avatar', 'newFan', 'totalSpent', 'role', 'isCreator', 'isPerformer', 'isRealPerformer']

function slimChat(chat: Raw): Raw {
  const m = obj(chat.lastMessage ?? chat.last_message)
  const text = String(m.text ?? '')
  const media = Array.isArray(m.media) ? m.media.slice(0, 1).map((x) => pick(obj(x), ['id', 'type'])) : []
  return {
    ...pick(chat, CHAT_KEYS),
    withUser: pick(obj(chat.withUser), USER_KEYS),
    lastMessage: {
      ...pick(m, ['id', 'createdAt', 'isFree', 'price', 'giphyId', 'isRead', 'type']),
      text: text.length > TEXT_MAX ? text.slice(0, TEXT_MAX) : text,
      fromUser: pick(obj(m.fromUser), ['id']),
      media,
    },
  }
}

function slimUser(u: Raw): Raw {
  const out = pick(u, USER_KEYS)
  const thumbs = obj(u.avatarThumbs)
  if (thumbs.c144 || thumbs.c50) out.avatarThumbs = { c144: thumbs.c144 || thumbs.c50 }
  const sub = obj(u.subscribedOnData)
  if (sub.totalSumm !== undefined) out.subscribedOnData = { totalSumm: sub.totalSumm }
  return out
}

function readStored(scope: string): Stored | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(PREFIX + scope) || 'null') as Stored | null
    return parsed && typeof parsed === 'object' && parsed.tabs && typeof parsed.tabs === 'object' ? parsed : null
  } catch {
    return null
  }
}

function readIndex(): string[] {
  try {
    const arr = JSON.parse(localStorage.getItem(INDEX) || '[]')
    return Array.isArray(arr) ? arr.map(String) : []
  } catch {
    return []
  }
}

export function readListCache(scope: string, tabKey: string): CachedTab | null {
  const stored = readStored(scope)
  const tab = stored?.tabs[tabKey]
  if (!tab || !Array.isArray(tab.list)) return null
  return { ...tab, users: stored.users ?? {} }
}

export function saveListCache(
  scope: string,
  tabKey: string,
  page: { list: Raw[]; hasMore: boolean; nextOffset?: number },
  users: Record<string, Raw> | Map<string, Raw>,
  now = Date.now(),
) {
  try {
    const stored = readStored(scope) ?? { tabs: {}, users: {} }
    const list = page.list.slice(0, KEEP_CHATS).map(slimChat)
    stored.tabs[tabKey] = {
      list,
      hasMore: page.hasMore || page.list.length > KEEP_CHATS,
      nextOffset: page.nextOffset,
      savedAt: now,
    }
    const byId = users instanceof Map ? Object.fromEntries(users) : users
    // Keep only the fans some cached tab still shows.
    const shown = new Set(Object.values(stored.tabs).flatMap((t) => t.list.map((c) => String(obj(c.withUser).id ?? c.withUserId ?? c.fan_id ?? ''))))
    const kept: Record<string, Raw> = {}
    for (const id of shown) {
      const u = byId[id] ?? stored.users[id]
      if (u) kept[id] = slimUser(obj(u))
    }
    stored.users = kept
    localStorage.setItem(PREFIX + scope, JSON.stringify(stored))
    const index = [scope, ...readIndex().filter((s) => s !== scope)]
    for (const old of index.splice(LIST_CACHE_ACCOUNTS)) localStorage.removeItem(PREFIX + old)
    localStorage.setItem(INDEX, JSON.stringify(index))
  } catch {
    /* storage full or unavailable: the page just loads without it next time */
  }
}

/** Logout: forget every Account's cached chats. */
export function clearListCaches() {
  try {
    const keys: string[] = []
    for (let i = 0; i < localStorage.length; i += 1) keys.push(localStorage.key(i) ?? '')
    for (const key of keys) if (key.startsWith(PREFIX)) localStorage.removeItem(key)
  } catch {
    /* storage unavailable */
  }
}
